import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Receipt,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertTriangle,
} from "lucide-react";

import api from "../../services/api";
import { formatMoney } from "../../helpers/money";

// Public page — where the gateway sends the customer back after checkout.
// Must not assume a session: it is opened in whatever browser paid.

const STATE_MESSAGES = {
  ok: {
    icon: Clock,
    tone: "text-indigo-600",
    text: "Estamos confirmando el pago con la pasarela. Puede tardar unos segundos.",
  },
  cancelado: {
    icon: XCircle,
    tone: "text-slate-400",
    text: "El pago fue cancelado. La factura sigue pendiente.",
  },
  rechazado: {
    icon: AlertTriangle,
    tone: "text-red-500",
    text: "La pasarela rechazó el pago. Intenta con otra tarjeta o contáctanos.",
  },
};

const InvoiceReceipt = () => {
  const { publicToken } = useParams();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const estado = params.get("estado");
  // Algunas pasarelas devuelven una referencia de la orden aprobada en ?token=.
  // Se manda al backend como pista; él verifica que sea de esta factura antes
  // de cobrar (hoy ninguna pasarela activa la usa, ver finalizeCheckout).
  const returnedReference = params.get("token") || undefined;

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/billing/invoices/${publicToken}`);
      setInvoice(data);
      return data;
    } catch {
      setError("No encontramos esta factura.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicToken]);

  // Al volver del checkout hay que CERRAR el pago, no solo esperarlo: para
  // pasarelas donde aprobar una orden no mueve dinero ni dispara webhook hasta
  // que el comercio la captura, sin este paso la factura quedaba "pendiente"
  // para siempre y el cobro nunca ocurría.
  //
  // Después se sigue consultando un rato, porque el webhook puede resolverlo
  // igual (o el pago puede venir de otra pasarela que sí cobra al aprobar).
  useEffect(() => {
    if (estado !== "ok") return undefined;

    let cancelled = false;
    let timer;

    const finalizeThenPoll = async () => {
      try {
        await api.post(
          `/public/billing/invoices/${publicToken}/finalize`,
          returnedReference ? { reference: returnedReference } : {}
        );
      } catch {
        // Da igual el motivo: el sondeo de abajo cubre el caso en que el
        // webhook lo resuelva por su cuenta.
      }

      if (cancelled) return;

      const data = await load();
      if (cancelled || data?.status === "paid") return;

      let tries = 0;
      timer = setInterval(async () => {
        tries += 1;
        const fresh = await load();
        if (tries >= 6 || fresh?.status === "paid") clearInterval(timer);
      }, 5000);
    };

    finalizeThenPoll();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, publicToken, returnedReference]);

  const paid = invoice?.status === "paid";
  const stateMeta = !paid && estado ? STATE_MESSAGES[estado] : null;

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-100 dark:bg-slate-950 px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-lg overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            <p className="text-sm text-slate-400">Cargando factura...</p>
          </div>
        ) : !invoice ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-3">
            <AlertTriangle className="w-7 h-7 text-slate-400" />
            <p className="text-base font-semibold text-slate-700 dark:text-slate-200">
              Enlace no válido
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">{error}</p>
          </div>
        ) : (
          <>
            <div className="px-6 pt-7 pb-5 text-center border-b border-slate-100 dark:border-slate-800">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                  paid
                    ? "bg-emerald-50 dark:bg-emerald-950/40"
                    : "bg-indigo-50 dark:bg-indigo-950/40"
                }`}
              >
                {paid ? (
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                ) : (
                  <Receipt className="w-6 h-6 text-indigo-600" />
                )}
              </div>
              <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {paid ? "Pago recibido" : `Factura ${invoice.number}`}
              </p>
              {invoice.clientName && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {invoice.clientName}
                  {invoice.clientRnc ? ` · RNC ${invoice.clientRnc}` : ""}
                </p>
              )}
            </div>

            <div className="px-6 py-5 space-y-2 bg-slate-50 dark:bg-slate-800/30">
              {(invoice.items || []).map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">
                    {item.description}
                  </span>
                  <span className="text-slate-900 dark:text-slate-100 tabular-nums">
                    {formatMoney(item.amountCents, invoice.currency)}
                  </span>
                </div>
              ))}
              {invoice.taxCents > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">ITBIS</span>
                  <span className="text-slate-900 dark:text-slate-100 tabular-nums">
                    {formatMoney(invoice.taxCents, invoice.currency)}
                  </span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Total
                </span>
                <span className="text-lg font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                  {formatMoney(invoice.totalCents, invoice.currency)}
                </span>
              </div>
            </div>

            <div className="px-6 py-5 text-center space-y-2">
              {paid ? (
                <p className="text-sm text-emerald-600">
                  Pagada el{" "}
                  {new Date(invoice.paidAt).toLocaleDateString("es-DO")}. El acceso
                  queda reactivado.
                </p>
              ) : stateMeta ? (
                <div className={`flex items-start gap-2 text-sm ${stateMeta.tone}`}>
                  <stateMeta.icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="text-left">{stateMeta.text}</span>
                </div>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Vence el {new Date(invoice.dueAt).toLocaleDateString("es-DO")}.
                </p>
              )}
              {invoice.ncf && (
                <p className="text-[11px] text-slate-400">NCF {invoice.ncf}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoiceReceipt;
